/**
 * Ribbon — the CAD ribbon shell (Faz 3.1/3.2/3.4/3.11/3.12).
 * Tab strip (flex-nowrap + overflow-x-auto) + active-tab panel row +
 * collapse control. Contextual tabs appear/disappear reactively:
 * Layout ⇔ paper space (useLayoutStore.activeId), Block Editor ⇔ the
 * isolated editing session (useBlockStore.editingBlock — engine lands with
 * Faz 5.15; until then the tab is simply never mounted).
 *
 * Collapse ladder (OCS CollapseMode): full → compact → collapsed → tight,
 * manual via the chevron button and Auto (width-driven) via the toggle.
 * Keytips (Alt chain): tabs → groups → items, Escape cancels anywhere.
 */

import { Box, Flex, Icon, IconButton, Tooltip } from '@chakra-ui/react';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, PanelTopClose, PanelTopOpen } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { RIBBON_TABS, type RibbonTabDef } from '../../../config/ribbonTabs';
import { useLayoutStore } from '../../../state/useLayoutStore';
import { useBlockStore } from '../../../state/commandAuxStores';
import { dynamicOwnsKeyEvent } from '../../../state/useDynamicInputStore';
import { RibbonPanel, type RibbonCollapseMode } from './RibbonPanel';
import { KeytipBadge, useTKey } from './KeytipBadge';
import { runRibbonBinding } from './ribbonCommand';
import { useKeytipsStore, type KeytipLevel } from './keytipsStore';

/** Auto-collapse thresholds (viewport px): below → compact → collapsed → tight. */
const AUTO_THRESHOLDS = { compact: 1400, collapsed: 1040, tight: 720 };

export type RibbonCollapseSetting = RibbonCollapseMode | 'auto';

interface RibbonProps {
    setting: RibbonCollapseSetting;
    onSettingChange: (s: RibbonCollapseSetting) => void;
}

/** Resolve the Auto ladder from the live viewport width. */
function autoMode(width: number): RibbonCollapseMode {
    if (width < AUTO_THRESHOLDS.tight) return 'tight';
    if (width < AUTO_THRESHOLDS.collapsed) return 'collapsed';
    if (width < AUTO_THRESHOLDS.compact) return 'compact';
    return 'full';
}

/** Order of the manual collapse ladder. */
const LADDER: RibbonCollapseMode[] = ['full', 'compact', 'collapsed', 'tight'];

/** Visible tabs for the current mode state (G10 mode-aware organization). */
function useVisibleTabs(): RibbonTabDef[] {
    const paperActive = useLayoutStore((s) => s.activeId !== null);
    const editingBlock = useBlockStore((s) => s.editingBlock);
    return useMemo(
        () => RIBBON_TABS.filter((tab) => {
            if (tab.hidden) return false; // Parametric: Faz 6 gate
            if (tab.contextual === 'paperSpace') return paperActive;
            if (tab.contextual === 'blockEditor') return editingBlock !== null;
            return true;
        }),
        [paperActive, editingBlock]
    );
}

/**
 * Global keytip keyboard chain: Alt arms it, letters drill through the
 * current level (tabs → groups → items), Escape cancels from anywhere.
 * Runs on the capture phase so the sequence buffer never races it.
 */
function useKeytipsKeyboard(tabs: RibbonTabDef[], activeTab: string) {
    const active = useKeytipsStore((s) => s.active);
    const level = useKeytipsStore((s) => s.level);
    const tabKey = useKeytipsStore((s) => s.tabKey);
    const groupKey = useKeytipsStore((s) => s.groupKey);

    useEffect(() => {
        const resolveChain = (letter: string): 'consumed' | 'miss' => {
            const s = useKeytipsStore.getState();
            if (s.level === 'tabs') {
                const hit = tabs.find((tb) => tb.keytip.toUpperCase() === letter);
                if (hit) { s.selectTab(hit.key); return 'consumed'; }
                return 'miss';
            }
            const tab = tabs.find((tb) => tb.key === s.tabKey);
            if (!tab) return 'miss';
            if (s.level === 'groups') {
                const hit = tab.groups.find((g) => g.keytip.toUpperCase() === letter);
                if (hit) { s.selectGroup(hit.key); return 'consumed'; }
                return 'miss';
            }
            const group = tab.groups.find((g) => g.key === s.groupKey);
            if (!group) return 'miss';
            const hit = group.items.find((it) => it.keytip?.toUpperCase() === letter);
            if (hit) {
                s.cancel();
                runRibbonBinding(hit);
                return 'consumed';
            }
            return 'miss';
        };

        // Input fields, dialogs and a running dynamic-input session own the
        // keyboard — the chain never arms there and cancels if left on.
        const keyboardOwned = (e: KeyboardEvent): boolean => {
            const el = e.target as HTMLElement | null;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return true;
            if (el?.closest?.('[role="dialog"]')) return true;
            return dynamicOwnsKeyEvent(e);
        };

        const onKeyDown = (e: KeyboardEvent) => {
            const s = useKeytipsStore.getState();
            if (keyboardOwned(e)) {
                if (s.active) s.cancel();
                return;
            }
            if (!s.active) {
                // Alt alone arms the chain (the keydown carries no letter yet).
                if (e.key === 'Alt' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    e.preventDefault();
                    s.start();
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                s.cancel();
                return;
            }
            if (e.key === 'Alt') {
                // Alt again leaves the chain.
                e.preventDefault();
                s.cancel();
                return;
            }
            if (e.altKey || e.ctrlKey || e.metaKey) return;
            if (e.key.length === 1 && /[a-z0-9]/i.test(e.key)) {
                if (resolveChain(e.key.toUpperCase()) === 'consumed') {
                    e.preventDefault();
                } else {
                    s.cancel();
                }
            }
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [tabs, active, level, tabKey, groupKey]);
}

export function Ribbon({ setting, onSettingChange }: RibbonProps) {
    const t = useTKey();
    const tabs = useVisibleTabs();
    const [activeTab, setActiveTab] = useState<string>(tabs[0]?.key ?? 'home');
    /** Open flyout: a single group (collapsed faces) or the whole tab (tight). */
    const [flyout, setFlyout] = useState<{ scope: 'tab' | 'group'; key: string } | null>(null);
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1920
    );

    // Keytips state.
    const keytips = useKeytipsStore();
    useKeytipsKeyboard(tabs, activeTab);

    // Keep the active tab valid as contextual tabs come and go.
    useEffect(() => {
        if (!tabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(tabs[0]?.key ?? 'home');
        }
    }, [tabs, activeTab]);

    // Viewport width observer for the Auto collapse ladder.
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const collapse: RibbonCollapseMode = setting === 'auto' ? autoMode(viewportWidth) : setting;
    const tab = tabs.find((tb) => tb.key === activeTab) ?? tabs[0];

    // Keytips: badges visible per current level.
    const tabBadges = keytips.active && keytips.level === 'tabs';
    const groupBadges = keytips.active && keytips.level === 'groups' && keytips.tabKey === activeTab;
    const itemBadges = keytips.active && keytips.level === 'items' && keytips.tabKey === activeTab;

    const stepDown = () => {
        const idx = LADDER.indexOf(collapse);
        onSettingChange(LADDER[Math.min(idx + 1, LADDER.length - 1)]);
    };
    const cycleLabel: Record<RibbonCollapseSetting, string> = {
        full: t('ribbon.collapse.toCompact'),
        compact: t('ribbon.collapse.toCollapsed'),
        collapsed: t('ribbon.collapse.toTight'),
        tight: t('ribbon.collapse.toFull'),
        auto: t('ribbon.collapse.auto'),
    };

    return (
        <Box
            className="h-ribbon"
            bg="bg.toolbar"
            borderBottom="1px solid"
            borderColor="border.base"
            position="relative"
            zIndex={0}
            flexShrink={0}
        >
            {/* Tab strip — horizontal scroll, tabs never shrink/wrap. */}
            <Flex
                h="32px"
                align="stretch"
                px={1}
                gap={0}
                flexWrap="nowrap"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: '4px' },
                    '&::-webkit-scrollbar-thumb': { background: 'border.base', borderRadius: '2px' },
                }}
            >
                {tabs.map((tb) => {
                    const isActive = tb.key === activeTab;
                    return (
                        <Box
                            key={tb.key}
                            as="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => {
                                setActiveTab(tb.key);
                                if (collapse === 'tight') {
                                    setFlyout((f) => (f?.scope === 'tab' && f.key === tb.key) ? null : { scope: 'tab', key: tb.key });
                                }
                            }}
                            position="relative"
                            display="flex"
                            alignItems="center"
                            px={3}
                            flexShrink={0}
                            whiteSpace="nowrap"
                            fontSize="12px"
                            fontWeight={isActive ? 'semibold' : 'normal'}
                            color={isActive ? 'text.primary' : 'text.secondary'}
                            borderBottom="2px solid"
                            borderColor={isActive ? 'brand.500' : 'transparent'}
                            _hover={{ color: 'text.primary', bg: 'bg.hover' }}
                        >
                            {t(tb.titleKey)}
                            {tb.contextual && (
                                <Box
                                    as="span"
                                    ml={1}
                                    w="5px"
                                    h="5px"
                                    borderRadius="full"
                                    bg="brand.500"
                                    display="inline-block"
                                />
                            )}
                            <KeytipBadge letter={tb.keytip} show={tabBadges} />
                        </Box>
                    );
                })}

                <Box flex="1" minWidth="8px" />

                {/* Collapse controls (fixed right slot). */}
                <Flex align="center" flexShrink={0} gap={0} pl={1}>
                    <Tooltip label={cycleLabel[setting]} placement="bottom" hasArrow>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={t('ribbon.collapse.collapse')}
                            icon={
                                collapse === 'full' ? <Icon as={ChevronsUpDown} boxSize="13px" />
                                    : collapse === 'compact' ? <Icon as={ChevronDown} boxSize="13px" />
                                        : <Icon as={ChevronsDownUp} boxSize="13px" />
                            }
                            onClick={stepDown}
                            color="icon.base"
                        />
                    </Tooltip>
                    <Tooltip label={t('ribbon.collapse.toggleAuto')} placement="bottom" hasArrow>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={t('ribbon.collapse.toggleAuto')}
                            icon={setting === 'auto'
                                ? <Icon as={PanelTopClose} boxSize="13px" color="brand.500" />
                                : <Icon as={PanelTopOpen} boxSize="13px" color="icon.base" />}
                            onClick={() => onSettingChange(setting === 'auto' ? 'full' : 'auto')}
                        />
                    </Tooltip>
                </Flex>
            </Flex>

            {/* Panel row (hidden entirely in tight mode: the tab flyout
                shows the panels on demand instead). */}
            {tab && collapse !== 'tight' && (
                <Flex
                    h="calc(100% - 32px)"
                    align="stretch"
                    overflowX="auto"
                    overflowY="hidden"
                    flexWrap="nowrap"
                    px={1}
                    pt={0}
                    sx={{
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { height: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: 'border.base', borderRadius: '2px' },
                    }}
                >
                    {tab.groups.map((group) => (
                        <RibbonPanel
                            key={group.key}
                            group={group}
                            collapse={collapse}
                            groupKeytipsOn={groupBadges}
                            itemKeytipsOn={itemBadges}
                            onFlyout={(key) => setFlyout(key ? { scope: 'group', key } : null)}
                            flyoutOpen={flyout?.scope === 'group' && flyout.key === group.key}
                        />
                    ))}
                </Flex>
            )}

            {/* Flyout overlay: collapsed group face or tight-tab panels. */}
            {tab && flyout && (collapse === 'collapsed' || collapse === 'tight') && (
                <Box
                    position="absolute"
                    top="32px"
                    left={0}
                    right={0}
                    bg="bg.panel"
                    borderBottom="1px solid"
                    borderColor="border.base"
                    zIndex={10}
                    boxShadow="md"
                    onMouseLeave={() => setFlyout(null)}
                >
                    <Flex align="stretch" px={1} h="84px" flexWrap="nowrap" overflowX="auto">
                        {(() => {
                            const groups = flyout.scope === 'tab'
                                ? tab.groups
                                : tab.groups.filter((g) => g.key === flyout.key);
                            return groups.map((group) => (
                                <RibbonPanel
                                    key={group.key}
                                    group={group}
                                    collapse="full"
                                    groupKeytipsOn={groupBadges}
                                    itemKeytipsOn={itemBadges}
                                    onFlyout={() => setFlyout(null)}
                                    flyoutOpen
                                />
                            ));
                        })()}
                    </Flex>
                </Box>
            )}

            {/* Keytips mode banner (visible hint; chain driven by listener). */}
            {keytips.active && (
                <Box
                    position="absolute"
                    top="2px"
                    left="50%"
                    transform="translateX(-50%)"
                    px={2}
                    py="1px"
                    bg="bg.active"
                    border="1px solid"
                    borderColor="border.base"
                    borderRadius="4px"
                    fontSize="10px"
                    color="text.primary"
                    pointerEvents="none"
                    zIndex={11}
                >
                    {t('ribbon.keytips.banner', {
                        level: t(`ribbon.keytips.level_${keytips.level satisfies KeytipLevel}`),
                    })}
                </Box>
            )}
        </Box>
    );
}
